/* PUNCH CART — V7.5 NO-FLICKER LIVE REFRESH + REMOVE KEY
   ------------------------------------------------------------
   Fixes the stale/empty drawer after AJAX add-to-cart:
   add -> fetch fresh server-rendered section -> refresh existing drawer -> open.

   Keeps:
   - popup/drawer cart journey
   - native quantity hooks
   - Shopify checkout
   - existing cart:change compatibility
   - Tidio hide/show behaviour

   Does NOT intercept checkout.
*/
(() => {
  if (window.__pwCartV71Bound) return;
  window.__pwCartV71Bound = true;

  const CART_DRAWER_ID = 'cart-drawer';
  const CHAT_ROOT_CLASS = 'pw-cart-open';

  let addInProgress = false;
  let removeInProgress = false;
  let refreshInProgress = null;
  let chatGuardTimer = null;
  const hiddenChatNodes = new Map();

  const getDrawer = () => document.getElementById(CART_DRAWER_ID);

  // A missing drawer must never turn a successful cart add into an error alert.
  function goToCart() {
    const root = window.Shopify?.routes?.root || '/';
    window.location.assign(root + 'cart');
  }

  function openAfterRefresh(refreshed) {
    if (!refreshed) {
      goToCart();
      return;
    }
    requestAnimationFrame(() => openCartDrawer());
  }

  function visibleArea(r) {
    const l = Math.max(0, r.left);
    const t = Math.max(0, r.top);
    const rr = Math.min(innerWidth, r.right);
    const b = Math.min(innerHeight, r.bottom);
    return Math.max(0, rr - l) * Math.max(0, b - t);
  }

  function drawerIsOpen() {
    const d = getDrawer();
    if (!d) return false;

    const s = getComputedStyle(d);
    if (
      s.display === 'none' ||
      s.visibility === 'hidden' ||
      parseFloat(s.opacity || '1') === 0 ||
      d.getAttribute('aria-hidden') === 'true'
    ) return false;

    if (
      d.hasAttribute('open') ||
      d.getAttribute('aria-hidden') === 'false' ||
      d.classList.contains('open') ||
      d.classList.contains('is-open') ||
      d.classList.contains('active')
    ) return true;

    const panel = d.shadowRoot?.querySelector('[part~="content"]');
    if (panel) {
      const r = panel.getBoundingClientRect();
      const a = r.width * r.height;
      if (a > 0 && visibleArea(r) / a > 0.45) return true;
    }

    const r = d.getBoundingClientRect();
    const a = r.width * r.height;
    return a > 0 && visibleArea(r) / a > 0.45;
  }

  function findCartTrigger() {
    for (const sel of [
      '[aria-controls="cart-drawer"]',
      'a[href="#cart-drawer"]',
      '[data-cart-drawer]'
    ]) {
      const el = [...document.querySelectorAll(sel)].find((x) =>
        x instanceof HTMLElement &&
        !x.closest('#cart-drawer') &&
        getComputedStyle(x).display !== 'none' &&
        getComputedStyle(x).visibility !== 'hidden'
      );
      if (el) return el;
    }
    return null;
  }

  function openCartDrawer() {
    const d = getDrawer();
    if (!d) return;

    if (drawerIsOpen()) {
      syncChat();
      return;
    }

    try {
      if (typeof d.show === 'function') {
        d.show();
        setTimeout(syncChat, 30);
        return;
      }
    } catch (_) {}

    try {
      if (typeof d.open === 'function') {
        d.open();
        setTimeout(syncChat, 30);
        return;
      }
    } catch (_) {}

    const trigger = findCartTrigger();
    if (trigger) {
      trigger.click();
      setTimeout(syncChat, 30);
      return;
    }

    console.warn('[PUNCH cart] Fresh cart loaded, but no drawer opener was found.');
  }

  async function getCurrentCart() {
    const root = window.Shopify?.routes?.root || '/';
    const r = await fetch(root + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });

    if (!r.ok) throw new Error('Unable to refresh the bag.');
    return r.json();
  }

  async function refreshDrawerFromServer() {
    if (refreshInProgress) return refreshInProgress;

    refreshInProgress = (async () => {
      const url = new URL(window.location.href);
      url.searchParams.set('_pw_cart_refresh', Date.now().toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { Accept: 'text/html' },
        cache: 'no-store',
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error('Unable to refresh the cart drawer.');
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const currentDrawer = getDrawer();

      if (!currentDrawer) {
        console.warn('[PUNCH cart] Drawer absent on this page; using cart page fallback.');
        return null;
      }

      const freshDrawer = doc.getElementById(CART_DRAWER_ID);

      if (!freshDrawer) {
        console.warn('[PUNCH cart] Server response has no drawer; using cart page fallback.');
        return null;
      }

      /*
        IMPORTANT:
        Keep the EXISTING <cart-drawer> custom element alive.
        Replacing the section wrapper (or the drawer element itself) makes
        Shopify close the drawer, then our code re-opens it — causing the
        visible shut/open flash after add or remove.

        Refresh only the drawer's light-DOM contents instead.
        The custom element, its open state, shadow DOM and upgraded instance
        remain in place.
      */
      currentDrawer.innerHTML = freshDrawer.innerHTML;
      return currentDrawer;
    })();

    try {
      return await refreshInProgress;
    } finally {
      refreshInProgress = null;
    }
  }

  function announceCartChange(cart) {
    document.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'cart:add',
        cart
      }
    }));
  }

  // Public bridge for custom product flows (graded returns / bundles).
  // Uses this drawer's existing refresh + open pipeline so those flows
  // never need to submit natively or redirect to /cart.
  async function addItemsAndOpen(items) {
    if (!Array.isArray(items) || !items.length) {
      throw new Error('No cart items were supplied.');
    }

    const root = window.Shopify?.routes?.root || '/';
    const addResponse = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items })
    });

    if (!addResponse.ok) {
      const err = await addResponse.json().catch(() => ({}));
      throw new Error(
        err.description ||
        err.message ||
        'Unable to add this item to your bag.'
      );
    }

    const cart = await getCurrentCart();
    const refreshed = await refreshDrawerFromServer();
    if (refreshed) announceCartChange(cart);
    openAfterRefresh(refreshed);

    return cart;
  }

  window.PunchCart = Object.assign(window.PunchCart || {}, {
    addItems: addItemsAndOpen,
    refreshAndOpen: async function () {
      const cart = await getCurrentCart();
      const refreshed = await refreshDrawerFromServer();
      if (refreshed) announceCartChange(cart);
      openAfterRefresh(refreshed);
      return cart;
    }
  });

  function isCartAddForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    const raw = form.getAttribute('action') || '';

    try {
      const path = new URL(raw || location.href, location.origin).pathname.replace(/\/+$/, '');
      return path.endsWith('/cart/add') || path.endsWith('/cart/add.js');
    } catch (_) {
      return raw.includes('/cart/add');
    }
  }

  function setButtonBusy(btn, busy) {
    if (!(btn instanceof HTMLElement)) return;

    if (busy) {
      btn.setAttribute('aria-busy', 'true');
      if ('disabled' in btn) btn.disabled = true;
    } else {
      btn.removeAttribute('aria-busy');
      if ('disabled' in btn) btn.disabled = false;
    }
  }


  function isDrawerRemoveLink(link) {
    if (!(link instanceof HTMLAnchorElement)) return false;
    if (!link.closest('#cart-drawer')) return false;

    const href = link.getAttribute('href') || '';

    try {
      const url = new URL(href, location.origin);
      return (
        url.pathname.endsWith('/cart/change') &&
        url.searchParams.get('quantity') === '0'
      );
    } catch (_) {
      return href.includes('/cart/change') && href.includes('quantity=0');
    }
  }

  document.addEventListener('click', async (event) => {
    const link = event.target.closest && event.target.closest('a[href]');
    if (!isDrawerRemoveLink(link)) return;

    event.preventDefault();
    event.stopPropagation();

    if (removeInProgress) return;
    removeInProgress = true;

    try {
      const url = new URL(link.href, location.origin);

      /*
        Shopify remove URLs are not guaranteed to use ?line=.
        Depending on the theme/cart state they may use an id/key instead.
        Prefer the native data-line-key hook already present on the quantity
        input, then fall back to the remove URL's id, then line number.
      */
      const lineItem = link.closest('line-item') || link.closest('.line-item');
      const quantityInput = lineItem && lineItem.querySelector(
        'input.quantity-input[data-line-key]'
      );

      const lineKey =
        quantityInput?.dataset?.lineKey ||
        url.searchParams.get('id') ||
        '';

      const lineNumber = parseInt(url.searchParams.get('line') || '', 10);

      const payload = { quantity: 0 };

      if (lineKey) {
        payload.id = lineKey;
      } else if (lineNumber) {
        payload.line = lineNumber;
      } else {
        throw new Error('Unable to identify this cart item.');
      }

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          err.description ||
          err.message ||
          'Unable to remove this item from your bag.'
        );
      }

      const cart = await response.json();

      const refreshed = await refreshDrawerFromServer();
      if (refreshed) announceCartChange(cart);
      openAfterRefresh(refreshed);
    } catch (err) {
      console.error('[PUNCH cart remove]', err);
      alert(err.message || 'Unable to remove this item from your bag.');
    } finally {
      removeInProgress = false;
    }
  }, true);

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!isCartAddForm(form)) return;

    // The custom PUNCH product form owns its graded-return / bundle flow.
    // Keep this global drawer interceptor away from that one specific form.
    if (
      form.matches('.pw-product-form') &&
      form.closest('.pw-prod-mock')
    ) return;

    event.preventDefault();
    event.stopPropagation();

    if (addInProgress) return;
    addInProgress = true;

    const submitter =
      event.submitter ||
      form.querySelector('button[type="submit"],input[type="submit"]');

    setButtonBusy(submitter, true);

    try {
      const fd = new FormData(form);

      if (event.submitter?.name && !fd.has(event.submitter.name)) {
        fd.append(event.submitter.name, event.submitter.value || '');
      }

      const root = window.Shopify?.routes?.root || '/';
      const addResponse = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd
      });

      if (!addResponse.ok) {
        const err = await addResponse.json().catch(() => ({}));
        throw new Error(
          err.description ||
          err.message ||
          'Unable to add this item to your bag.'
        );
      }

      /* Critical order:
         1) Shopify has accepted the item.
         2) Get the current cart.
         3) Replace stale drawer HTML with a fresh server render.
         4) Tell theme listeners cart changed.
         5) Open the NEW drawer.
      */
      const cart = await getCurrentCart();
      const refreshed = await refreshDrawerFromServer();
      if (refreshed) announceCartChange(cart);
      openAfterRefresh(refreshed);

      if (submitter && form.matches('[data-pw-cart-rec-form]')) {
        const original = submitter.textContent;
        submitter.textContent = '✓';
        setTimeout(() => {
          submitter.textContent = original || '+';
        }, 900);
      }
    } catch (err) {
      console.error('[PUNCH cart]', err);
      alert(err.message || 'Unable to add this item to your bag.');
    } finally {
      addInProgress = false;
      setTimeout(
        () => setButtonBusy(submitter, false),
        form.matches('[data-pw-cart-rec-form]') ? 900 : 150
      );
    }
  }, true);

  /* TIDIO — preserve current behaviour, but avoid scanning the full DOM
     every 120ms. We only query known chat-like selectors while the drawer
     is actually open. */
  function looksLikeChatNode(node) {
    if (!(node instanceof Element)) return false;

    const v = `${node.id || ''} ${
      typeof node.className === 'string' ? node.className : ''
    } ${node.getAttribute?.('title') || ''} ${
      node.getAttribute?.('aria-label') || ''
    } ${node.getAttribute?.('src') || ''}`.toLowerCase();

    return (
      v.includes('tidio') ||
      v.includes('tidiochat') ||
      v.includes('chat widget') ||
      v.includes('chat-button') ||
      v.includes('need some help')
    );
  }

  function collectChatNodes() {
    const nodes = new Set();
    const sels = [
      '#tidio-chat',
      '#tidio-chat-iframe',
      '#tidio-chat-code',
      '[id*="tidio" i]',
      '[class*="tidio" i]',
      '[data-tidio-chat]',
      'iframe[src*="tidio.co"]',
      'iframe[src*="tidiochat.com"]',
      'iframe[title*="chat" i]',
      '[aria-label*="chat" i]'
    ];

    try {
      document.querySelectorAll(sels.join(',')).forEach((n) => {
        if (looksLikeChatNode(n)) nodes.add(n);
      });
    } catch (_) {}

    return [...nodes];
  }

  function hideNode(n) {
    if (!(n instanceof HTMLElement)) return;

    if (!hiddenChatNodes.has(n)) {
      hiddenChatNodes.set(n, {
        display: n.style.getPropertyValue('display'),
        displayPriority: n.style.getPropertyPriority('display'),
        visibility: n.style.getPropertyValue('visibility'),
        visibilityPriority: n.style.getPropertyPriority('visibility'),
        opacity: n.style.getPropertyValue('opacity'),
        opacityPriority: n.style.getPropertyPriority('opacity'),
        pointerEvents: n.style.getPropertyValue('pointer-events'),
        pointerPriority: n.style.getPropertyPriority('pointer-events')
      });
    }

    n.style.setProperty('display', 'none', 'important');
    n.style.setProperty('visibility', 'hidden', 'important');
    n.style.setProperty('opacity', '0', 'important');
    n.style.setProperty('pointer-events', 'none', 'important');
  }

  function restoreNode(n, previous) {
    if (!(n instanceof HTMLElement)) return;

    const restore = (prop, val, priority) => {
      if (val) n.style.setProperty(prop, val, priority || '');
      else n.style.removeProperty(prop);
    };

    restore('display', previous.display, previous.displayPriority);
    restore('visibility', previous.visibility, previous.visibilityPriority);
    restore('opacity', previous.opacity, previous.opacityPriority);
    restore('pointer-events', previous.pointerEvents, previous.pointerPriority);
  }

  function hideChat() {
    document.documentElement.classList.add(CHAT_ROOT_CLASS);
    try { window.tidioChatApi?.hide?.(); } catch (_) {}
    collectChatNodes().forEach(hideNode);
  }

  function showChat() {
    document.documentElement.classList.remove(CHAT_ROOT_CLASS);

    hiddenChatNodes.forEach((previous, node) => {
      if (node.isConnected) restoreNode(node, previous);
    });

    hiddenChatNodes.clear();
    try { window.tidioChatApi?.show?.(); } catch (_) {}
  }

  function stopChatGuard() {
    if (chatGuardTimer) {
      clearInterval(chatGuardTimer);
      chatGuardTimer = null;
    }
  }

  function startChatGuard() {
    if (chatGuardTimer) return;

    hideChat();

    chatGuardTimer = setInterval(() => {
      if (!drawerIsOpen()) {
        stopChatGuard();
        showChat();
        return;
      }

      hideChat();
    }, 500);
  }

  function syncChat() {
    if (drawerIsOpen()) {
      startChatGuard();
    } else {
      stopChatGuard();
      if (hiddenChatNodes.size) showChat();
    }
  }

  document.addEventListener('tidioChat-ready', syncChat);
  document.addEventListener('click', () => {
    requestAnimationFrame(syncChat);
    setTimeout(syncChat, 100);
  }, true);

  document.addEventListener('cart:change', () => {
    requestAnimationFrame(syncChat);
  });

  addEventListener('pageshow', syncChat);
  addEventListener('resize', syncChat);

  syncChat();
})();
